import React from "react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import KPICard from "../KPICard/KPICard";
import { useMemberDashboardPage } from "../../hooks/useMemberDashboardPage";
import { KPI_CONFIG, kpiElementId, SECTIONS } from "../../config/kpiConfig";

/**
 * Member KPI dashboard — the SOAR KPIs in plain English.
 *
 * Sections 01 (Profit & Loss) and 02 (Balance Sheet) are wired today. Sales &
 * Operations renders through the same loop as its calculations land — a section
 * with no KPIs is skipped, so declaring one early leaves no empty heading.
 */

/**
 * How many placeholder cards the grid holds while the request is in flight.
 *
 * **No count can match every member.** The API sends the Sales & Operations
 * cards only to a member who has connected ServiceTitan, so the page is ten
 * cards for one member and fifteen for another — and the skeleton only ever
 * renders before the first response, when which of the two this is is not yet
 * known. A constant claiming "the grid does not reflow" was wrong at ten and
 * is wrong at fifteen; it is the claim that cannot hold, not the number.
 *
 * So this reserves the floor — the cards every member receives — and the
 * difference is made up by the page growing downwards, which moves nothing
 * already on screen. Reserving the ceiling instead snaps the page upwards by
 * five cards for every member without the integration.
 *
 * Derived rather than typed: as a literal it went stale the first time a KPI
 * was added.
 */
const SKELETON_CARDS = Object.values(KPI_CONFIG).filter(
  (config) => !SECTIONS[config.section].requiresIntegration,
).length;

const MemberDashboardPage: React.FC = () => {
  const { sections, periodLabel, isLoading, loadProblem } =
    useMemberDashboardPage();

  return (
    <MainPageWrapper
      /*
        The months, not a control. There is no window to choose — the API's
        default is the only one the page asks for — so the header states which
        months these are and nothing more. Null until the first response, which
        is the only time the page has nothing to state.
      */
      actions={
        periodLabel && (
          <span
            className="text-xs text-gray-500 dark:text-gray-400"
            data-testid="period-label"
          >
            {periodLabel}
          </span>
        )
      }
      title="Member Dashboard"
    >
      <div className="space-y-10">
        <h2 className="text-2xl font-semibold tracking-tight text-secondary dark:text-white">
          Key Performance Indicators
        </h2>

        {loadProblem && (
          <p
            className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
            role="alert"
          >
            {loadProblem}
          </p>
        )}

        {/*
          There is one fetch, on mount, so the skeleton and a populated section
          list can never overlap. The `sections.length` guard is what makes that
          true rather than assumed.
        */}
        {isLoading && sections.length === 0 && (
          <div className="grid items-start gap-6 [grid-template-columns:repeat(auto-fill,minmax(26rem,1fr))]">
            {Array.from({ length: SKELETON_CARDS }, (_, index) => (
              <div
                className="min-h-[32rem] animate-pulse rounded-lg border border-gray-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800"
                data-testid="kpi-skeleton-card"
                key={index}
              >
                <div className="h-3 w-32 rounded bg-gray-200 dark:bg-slate-700" />
                <div className="mt-6 h-6 w-40 rounded bg-gray-200 dark:bg-slate-700" />
                <div className="mt-4 h-12 w-48 rounded bg-gray-200 dark:bg-slate-700" />
                <div className="mt-8 h-40 rounded bg-gray-100 dark:bg-slate-700/50" />
              </div>
            ))}
          </div>
        )}

        <div className="space-y-10" data-testid="kpi-sections">
          {sections.map((section) => (
            <section key={section.code}>
              <div className="flex flex-wrap items-baseline gap-3">
                <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                  [{section.ordinal}]
                </span>
                <h2 className="text-2xl font-bold tracking-tight text-secondary dark:text-white">
                  {section.title}
                </h2>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {section.kpis.length}{" "}
                  {section.kpis.length === 1 ? "metric" : "metrics"} · from{" "}
                  {section.source}
                </span>
              </div>
              <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-300">
                {section.blurb}
              </p>

              <div className="mt-6 grid items-start gap-6 [grid-template-columns:repeat(auto-fill,minmax(26rem,1fr))]">
                {section.kpis.map((kpi) => (
                  /* `key` is React bookkeeping; the id is what makes a card
                     addressable from outside. The account mapping page links
                     here to show a member the card a ledger row feeds. */
                  <div id={kpiElementId(kpi.code)} key={kpi.code}>
                    <KPICard kpi={kpi} />
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {!isLoading && !loadProblem && sections.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No KPIs are available yet. Once QuickBooks or ServiceTitan is
            connected, your numbers will appear here.
          </p>
        )}
      </div>
    </MainPageWrapper>
  );
};

export default MemberDashboardPage;
